import React, { useState } from 'react';
import Papa from 'papaparse';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '../../../lib/supabaseClient';
import { detectDistrict } from '../../Utils/districtUtils';

// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const ITCProcessor = () => {
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [eventType, setEventType] = useState('Oui Design! 2025'); // Default to Oui Design! 2025
    const [previewData, setPreviewData] = useState(null);

    // Initialize Mapbox token
    mapboxgl.accessToken = 'pk.eyJ1IjoiZGdjb2Jvc3MiLCJhIjoiY2xzY2JnajIzMGZsNzJpcGM4b3l5OWttaCJ9.1GzkF8EERgQ5u1jkmP3C7w';

    // Function to get coordinates from address using Mapbox
    const getCoordinates = async (address) => {
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}`
            );
            const data = await response.json();
            
            if (data.features && data.features.length > 0) {
                const [longitude, latitude] = data.features[0].center;
                return { longitude, latitude };
            }
            return null;
        } catch (error) {
            console.error('Geocoding error:', error);
            return null;
        }
    };

    // Add delay between API calls to respect rate limits
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Parse date string to standardized format
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            // Handle various date formats
            dateStr = dateStr.trim();
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
            }
            return dateStr; // Return original if cannot parse
        } catch (e) {
            return dateStr;
        }
    };

    const processFile = async (file) => {
        setProcessing(true);
        setError('');
        setFeedback('');
        setPreviewData(null);

        const reader = new FileReader();
        reader.onload = async (e) => {
            const text = e.target.result;
            
            Papa.parse(text, {
                header: true,
                delimiter: ';', // Set delimiter to semicolon for your CSV
                skipEmptyLines: true,
                preview: 5, // Preview first 5 rows for display
                complete: async (previewResults) => {
                    // Set preview data for display
                    setPreviewData(previewResults.data);
                    
                    // Now process the full file
                    Papa.parse(text, {
                        header: true,
                        delimiter: ';', // Set delimiter to semicolon for your CSV
                        skipEmptyLines: true,
                        complete: async (results) => {
                            try {
                                const total = results.data.length;
                                setProgress({ current: 0, total });
                                
                                const processedData = [];
                                const errorEntries = [];
                                
                                for (let i = 0; i < results.data.length; i++) {
                                    const row = results.data[i];
                                    
                                    // Map CSV columns to Supabase columns - specific to your CSV format
                                    const mappedData = {
                                        nom_etablissement: row['Raison sociale (Société)'] || row.nom_etablissement || '',
                                        adresse: row['Adresse complète (AdresseShowroom)'] || row.adresse || row.addresse || '',
                                        description_projet: row['Votre texte de présentation (Français)'] || row.description_projet || '',
                                        site_web: (row['Site web (Société)'] || row.site_web || '').trim(),
                                        instagram: (row['instagram '] || row.instagram || '').trim(),
                                        event_type: row.type_evenement || 'Exhibition',
                                        design_events: row.design_events || eventType,
                                        date_debut_expo: formatDate(row.date || ''),
                                        date_fin_expo: formatDate(row.date || ''), // Same as start date if no end date provided
                                        vernissage_date: formatDate(row.date || ''),
                                        vernissage_hour: row.horaires || '',
                                        contact_rsvp: row.contact_rsvp || '',
                                        rsvp: row.rsvp || '',
                                        district: row['quartier '] || row.quartier || row.Quartier || row.district || '' // Add district from CSV
                                    };
                                    
                                    // If district is not provided in CSV, try to detect it from address
                                    if (!mappedData.district && mappedData.adresse) {
                                        const detectedDistrict = detectDistrict(mappedData.adresse);
                                        if (detectedDistrict) {
                                            mappedData.district = detectedDistrict.name;
                                        }
                                    }

                                    if (mappedData.adresse) {
                                        try {
                                            const coords = await getCoordinates(mappedData.adresse);
                                            if (coords) {
                                                mappedData.longitude = coords.longitude.toString();
                                                mappedData.latitude = coords.latitude.toString();
                                                
                                                // Clean up URLs
                                                if (mappedData.site_web) {
                                                    if (!/^https?:\/\//i.test(mappedData.site_web)) {
                                                        mappedData.site_web = 'https://' + mappedData.site_web;
                                                    }
                                                }

                                                processedData.push(mappedData);
                                            } else {
                                                errorEntries.push({
                                                    enseigne: mappedData.nom_etablissement,
                                                    error: 'Could not get coordinates'
                                                });
                                            }
                                        } catch (err) {
                                            errorEntries.push({
                                                enseigne: mappedData.nom_etablissement,
                                                error: err.message
                                            });
                                        }
                                        
                                        await delay(1000); // Respect Mapbox rate limits
                                    }

                                    setProgress({ current: i + 1, total });
                                }

                                // Upload to Supabase
                                if (processedData.length > 0) {
                                    try {
                                        const chunkSize = 10; // Reduced chunk size for testing
                                        let successCount = 0;

                                        for (let i = 0; i < processedData.length; i += chunkSize) {
                                            const chunk = processedData.slice(i, i + chunkSize);
                                            
                                            console.log('Uploading chunk:', i / chunkSize + 1);
                                            console.log('Sample data from chunk:', chunk[0]);

                                            const { data, error: supabaseError } = await supabase
                                                .from('itc_2025')
                                                .insert(chunk);

                                            if (supabaseError) {
                                                console.error('Supabase insertion error:', {
                                                    message: supabaseError.message,
                                                    details: supabaseError.details,
                                                    hint: supabaseError.hint,
                                                    code: supabaseError.code
                                                });
                                                throw new Error(`Supabase error: ${supabaseError.message || 'Unknown error'}`);
                                            }

                                            successCount += chunk.length;
                                            console.log(`Successfully inserted chunk. Total success: ${successCount}`);
                                            
                                            // Add a small delay between chunks
                                            await delay(500);
                                        }

                                        setFeedback(
                                            `Successfully uploaded ${successCount} entries.${
                                                errorEntries.length > 0 
                                                    ? ` Failed to process ${errorEntries.length} entries.`
                                                    : ''
                                            }`
                                        );

                                        if (errorEntries.length > 0) {
                                            console.log('Entries with errors:', errorEntries);
                                        }
                                    } catch (supabaseError) {
                                        console.error('Full Supabase error:', supabaseError);
                                        setError(`Database error: ${supabaseError.message}`);
                                    }
                                } else {
                                    setError('No valid entries were found to process');
                                }
                            } catch (error) {
                                console.error('Full error details:', error);
                                setError(`Error: ${error.message || 'Unknown error occurred'}`);
                            } finally {
                                setProcessing(false);
                            }
                        },
                        error: (error) => {
                            console.error('CSV parsing error:', error);
                            setError(`CSV parsing error: ${error.message}`);
                            setProcessing(false);
                        }
                    });
                },
                error: (error) => {
                    console.error('CSV preview error:', error);
                    setError(`CSV parsing error: ${error.message}`);
                    setProcessing(false);
                }
            });
        };

        reader.onerror = () => {
            setError('Error reading file');
            setProcessing(false);
        };

        // Read with Windows-1252 encoding (common for French Excel/CSV exports)
        reader.readAsText(file, 'windows-1252');
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            processFile(file);
        }
    };

    const handleEventTypeChange = (e) => {
        setEventType(e.target.value);
    };

    const renderPreviewData = () => {
        if (!previewData || previewData.length === 0) return null;
        
        return (
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <h4>Data Preview (First 5 rows)</h4>
                <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px'
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Establishment</th>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Address</th>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Event Type</th>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>District</th>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '8px', borderBottom: '2px solid #ddd', textAlign: 'left' }}>Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {previewData.map((row, index) => (
                                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.nom_etablissement}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.addresse}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.type_evenement}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.district || detectDistrict(row.addresse)?.name || '-'}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.date}</td>
                                    <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{row.horaires}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    The above data will be processed and mapped to the Supabase database fields.
                </p>
            </div>
        );
    };

    const testSupabaseConnection = async () => {
        setError('');
        setFeedback('');
        setProcessing(true);
    
        try {
            // First, test if we can query the table
            console.log('Testing table access...');
            const { data: tableTest, error: tableError } = await supabase
                .from('itc_2025')
                .select('*')
                .limit(1);
    
            if (tableError) {
                console.error('Table access error:', tableError);
                throw new Error(`Table access error: ${tableError.message}`);
            }
    
            console.log('Table access successful:', tableTest);
    
            // Now try to insert test data
            const testData = {
                nom_etablissement: "Test Oui Design Location",
                adresse: "123 Design St, Paris",
                description_projet: "Test Oui Design event description",
                site_web: "https://ouiDesign.example.com",
                design_events: eventType,
                event_type: "Exhibition",
                date_debut_expo: "2025-05-15",
                date_fin_expo: "2025-05-15",
                vernissage_date: "2025-05-15",
                vernissage_hour: "6pm-8pm",
                contact_rsvp: "test@example.com",
                rsvp: "https://rsvp.example.com",
                longitude: "2.3522",
                latitude: "48.8566",
                district: "Test District"
            };
    
            console.log('Attempting to insert test data:', testData);
    
            const { data: insertData, error: insertError } = await supabase
                .from('itc_2025')
                .insert([testData]);
    
            if (insertError) {
                console.error('Insert error details:', {
                    message: insertError.message,
                    details: insertError.details,
                    hint: insertError.hint,
                    code: insertError.code
                });
                throw new Error(`Insert failed: ${insertError.message}`);
            }
    
            console.log('Insert successful:', insertData);
            setFeedback('Test connection successful! Test row inserted.');
    
        } catch (error) {
            console.error('Full test error:', error);
            setError(`Connection test failed: ${error.message}`);
        } finally {
            setProcessing(false);
        }
    };

    const checkTableStructure = async () => {
        try {
            // Get table definition
            const { data, error } = await supabase
                .rpc('get_table_def', { table_name: 'itc_2025' });
    
            if (error) {
                console.error('Error getting table structure:', error);
                setError(`Error getting table structure: ${error.message}`);
                return;
            }
    
            console.log('Table structure:', data);
            alert("Table structure logged to console. Check browser developer tools.");
        } catch (error) {
            console.error('Error:', error);
            setError(`Error checking table structure: ${error.message}`);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h2>Process Design Events Data</h2>
            
            <div style={{ 
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5'
            }}>
                <h3>Oui Design! 2025 Events Processor</h3>
                <p>This tool will process your CSV file and upload the events to the map.</p>
                
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Event Category:
                    </label>
                    <select 
                        value={eventType} 
                        onChange={handleEventTypeChange}
                        style={{ 
                            padding: '8px', 
                            width: '100%', 
                            marginBottom: '15px',
                            borderRadius: '4px',
                            border: '1px solid #ccc'
                        }}
                    >
                        <option value="Oui Design! 2025">Oui Design! 2025</option>
                        <option value="In the City">In the City</option>
                        <option value="In The City 2026">In The City 2026</option>
                        <option value="Paris Design Week 2025">Paris Design Week 2025</option>
                    </select>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Upload CSV File:
                    </label>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                        Make sure your CSV uses semicolons (;) as separators. Supports either standard format 
                        (nom_etablissement, addresse, etc.) or Paris Design Week format with columns: 
                        "Raison sociale (Société)", "Adresse complète (AdresseShowroom)", 
                        "Votre texte de présentation (Français)", "Site web (Société)", "Quartier"
                    </p>
                    <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        disabled={processing}
                        style={{ 
                            marginBottom: '10px',
                            padding: '10px',
                            width: '100%',
                            border: '1px solid #ccc',
                            borderRadius: '4px'
                        }}
                    />
                </div>
                
                {renderPreviewData()}
            </div>

            {processing && (
                <div style={{ 
                    marginBottom: '20px',
                    padding: '10px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px'
                }}>
                    <p>Processing... {progress.current} of {progress.total}</p>
                    <div style={{ 
                        width: '100%',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${(progress.current / progress.total) * 100}%`,
                            height: '100%',
                            backgroundColor: '#2196f3',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </div>
            )}

            {feedback && (
                <div style={{ 
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    borderRadius: '4px'
                }}>
                    {feedback}
                </div>
            )}

            {error && (
                <div style={{ 
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    borderRadius: '4px'
                }}>
                    {error}
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                    onClick={testSupabaseConnection}
                    disabled={processing}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processing ? 'not-allowed' : 'pointer',
                        flex: '1'
                    }}
                >
                    Test Connection
                </button>

                <button
                    onClick={checkTableStructure}
                    disabled={processing}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: processing ? 'not-allowed' : 'pointer',
                        flex: '1'
                    }}
                >
                    Check Table Structure
                </button>
            </div>
        </div>
    );
};

export default ITCProcessor;